import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { fetchCommunities, joinCommunity } from '../../services/communityService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUsers, 
  faSearch, 
  faPlus, 
  faCheckCircle, 
  faDoorOpen,
  faUserCog,
  faCrown,
  faLock,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";
// Import the event types utility for translations
import { getEventTypeDisplayName } from '../../utils/EventTypesUtil';

function CommunitiesList() {
  const [communities, setCommunities] = useState([]);
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [joiningCommunity, setJoiningCommunity] = useState(null);
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { userCommunities, refreshCsrfToken, isMemberOfCommunity, isAdminOfCommunity } = useAuth();
  const { isMobile } = useNavigation();
  const { t } = useLanguage();
  
  // Load all communities
  useEffect(() => {
    async function loadCommunities() {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetchCommunities();
        if (response && response.communities) {
          setCommunities(response.communities);
          setFilteredCommunities(response.communities);
        }
      } catch (err) {
        setError('Napaka pri pridobivanju seznama skupnosti.');
      } finally {
        setLoading(false);
      }
    }
    
    loadCommunities();
  }, []);
  
  // Filter communities based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCommunities(communities);
    } else {
      const filtered = communities.filter(community => 
        community.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (community.description && community.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCommunities(filtered);
    }
  }, [searchTerm, communities]);
  
  const handleJoinCommunity = async (communityId) => {
    try {
      setJoiningCommunity(communityId);
      setError('');
      setSuccess('');
      
      // Refresh CSRF token
      await refreshCsrfToken();
      
      // Join the community
      await joinCommunity(communityId, false);
      
      setSuccess('Uspešno ste se pridružili skupnosti!');
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Refresh to update auth context
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.error || 
        'Prišlo je do napake pri pridruževanju skupnosti. Prosimo, poskusite znova.'
      );
    } finally {
      setJoiningCommunity(null);
    }
  };

  // Updated function to handle clicking on a community
  const handleCommunityClick = (communityId) => {
    // If the user is a member, navigate to the community view
    if (isMemberOfCommunity(communityId)) {
      navigate(`/community/${communityId}`);
    } else {
      // If not a member, join the community
      handleJoinCommunity(communityId);
    }
  };

  const handleManageCommunity = (communityId) => {
    navigate(`/manage-community/${communityId}`);
  };
  
  // Helper to render visibility badge
  const renderVisibilityBadge = (visibility) => {
    if (visibility === 'Private') {
      return (
        <Badge bg="secondary" className="me-2">
          <FontAwesomeIcon icon={faLock} className="me-1" />
          {!isMobile && "Zasebna"}
        </Badge>
      );
    } else {
      // Treat any non-Private (including empty string) as Public
      return (
        <Badge bg="info" className="me-2">
          <FontAwesomeIcon icon={faGlobe} className="me-1" />
          {!isMobile && "Javna"}
        </Badge>
      );
    }
  };
  
  // Helper to parse event types safely and return an array
  const parseEventTypes = (eventTypesJson) => {
    try {
      return JSON.parse(eventTypesJson || '[]');
    } catch (e) {
      return [];
    }
  };

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="mobile-song-library">
        <div className="mobile-song-header">
          <div className="mobile-song-header-content">
            <h1>Vse skupine</h1>
            <div className="mobile-song-actions">
              <button 
                className="mobile-header-button"
                onClick={() => navigate('/community/create')}
                aria-label="Ustvari novo skupino"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>
          <div className="mobile-search-container">
            <input 
              type="text" 
              className="mobile-search-input" 
              placeholder="Išči po imenu ali opisu..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {error && <Alert variant="danger" className="mx-3 mt-2 p-2">{error}</Alert>}
        {success && <Alert variant="success" className="mx-3 mt-2 p-2">{success}</Alert>}
        
        <div className="mobile-song-content">
          {loading ? (
            <div className="mobile-song-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Nalaganje...</span>
              </div>
            </div>
          ) : filteredCommunities.length === 0 ? (
            <div className="mobile-song-empty">
              <p className="text-muted">
                {searchTerm ? 'Ni skupnosti, ki ustrezajo iskalnemu nizu.' : 'Trenutno ni nobenih skupnosti.'}
              </p>
            </div>
          ) : (
            <div className="mobile-song-list">
              {filteredCommunities.map(community => (
                <div key={community.id} className="mobile-song-item">
                  <div 
                    className="mobile-song-item-content"
                    onClick={() => handleCommunityClick(community.id)}
                  >
                    <div className="mobile-song-icon">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="mobile-song-details">
                      <div className="mobile-song-title">{community.name}</div>
                      <div className="mobile-song-meta">
                        <div className="mobile-song-composer">
                          {community.description?.length > 60 
                            ? `${community.description.substring(0, 60)}...` 
                            : community.description || 'Ni opisa'}
                        </div>
                      </div>
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        {renderVisibilityBadge(community.visibility)}
                        {isMemberOfCommunity(community.id) && (
                          <Badge bg="success">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </Badge>
                        )}
                      </div>
                      
                      {/* Event Types in Slovenian (mobile view) */}
                      {community.supported_event_types && parseEventTypes(community.supported_event_types).length > 0 && (
                        <div className="mt-1 d-flex flex-wrap gap-1" style={{ fontSize: '0.7rem' }}>
                          {parseEventTypes(community.supported_event_types).slice(0, 3).map((type, index) => (
                            <Badge key={index} bg="light" text="dark" className="small">
                              {getEventTypeDisplayName(type, t)}
                            </Badge>
                          ))}
                          {parseEventTypes(community.supported_event_types).length > 3 && (
                            <Badge bg="light" text="dark" className="small">
                              +{parseEventTypes(community.supported_event_types).length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="mobile-song-options">
                      {isAdminOfCommunity(community.id) ? (
                        <FontAwesomeIcon 
                          icon={faUserCog} 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageCommunity(community.id);
                          }}
                        />
                      ) : isMemberOfCommunity(community.id) ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                      ) : (
                        <FontAwesomeIcon 
                          icon={faDoorOpen} 
                          className={joiningCommunity === community.id ? "fa-spin" : ""}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Floating action button for creating a new community */}
        <button 
          className="mobile-action-button"
          onClick={() => navigate('/community/create')}
          aria-label="Ustvari novo skupino"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
    );
  }

  // DESKTOP VIEW - with translated event types
  return (
    <Container className="py-5">
      <Card className="shadow border-0 rounded-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Vse skupine</h2>
            <Button 
              variant="primary"
              onClick={() => navigate('/community/create')}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Ustvari novo skupino
            </Button>
          </div>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form className="mb-3">
            <Form.Group>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Išči po imenu ali opisu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Form.Group>
          </Form>
          
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Nalaganje skupnosti...</p>
            </div>
          ) : filteredCommunities.length === 0 ? (
            <Alert variant="info">
              {searchTerm ? 'Ni skupnosti, ki ustrezajo iskalnemu nizu.' : 'Trenutno ni nobenih skupnosti.'}
            </Alert>
          ) : (
            <Row xs={1} md={2} className="g-4">
              {filteredCommunities.map(community => (
                <Col key={community.id}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Card.Title>{community.name}</Card.Title>
                        <div>
                          {renderVisibilityBadge(community.visibility)}
                          {isMemberOfCommunity(community.id) && (
                            <Badge bg="success" text="white">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                              Član
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Card.Text>
                        {community.description || 'Ni opisa.'}
                      </Card.Text>
                      {community.allow_member_edits && (
                        <div className="mb-2">
                          <Badge bg="primary" pill>
                            Člani lahko urejajo vsebino
                          </Badge>
                        </div>
                      )}
                      
                      {/* Updated to display event types in Slovenian */}
                      {community.supported_event_types && parseEventTypes(community.supported_event_types).length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted">Podprte vrste dogodkov:</small>
                          <div className="mt-1">
                            {parseEventTypes(community.supported_event_types).map((type, index) => (
                              <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                                {getEventTypeDisplayName(type, t)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                    <Card.Footer className="bg-transparent border-0 d-flex justify-content-end">
                      {isAdminOfCommunity(community.id) ? (
                        <Button 
                          variant="primary"
                          size="sm"
                          onClick={() => handleManageCommunity(community.id)}
                        >
                          <FontAwesomeIcon icon={faUserCog} className="me-2" />
                          Upravljaj
                        </Button>
                      ) : isMemberOfCommunity(community.id) ? (
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          onClick={() => navigate(`/community/${community.id}`)}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Oglej si
                        </Button>
                      ) : (
                        <Button 
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleJoinCommunity(community.id)}
                          disabled={joiningCommunity === community.id}
                        >
                          {joiningCommunity === community.id ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Pridruževanje...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faDoorOpen} className="me-2" />
                              Pridruži se
                            </>
                          )}
                        </Button>
                      )}
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default CommunitiesList;